import { getAddress, type Eip1193Provider } from "ethers";
import type { WalletState } from "../types";
import { BaseWalletAdapter } from "./BaseWalletAdapter";
import { SurfError, SurfErrorCode } from "../utils/errors";

export class WalletConnectAdapter extends BaseWalletAdapter {
  readonly name = "walletconnect";

  constructor(private readonly providerFactory?: () => Eip1193Provider | undefined) {
    super();
  }

  get installed(): boolean {
    return Boolean(this.resolveProvider());
  }

  protected resolveProvider(): Eip1193Provider | undefined {
    return this.providerFactory?.();
  }

  override async connect(chainId: number): Promise<WalletState> {
    if (!this.providerFactory) {
      throw new SurfError(
        SurfErrorCode.WALLET_NOT_INSTALLED,
        "WalletConnect requires a provider factory during SDK setup",
      );
    }

    const provider = this.requireProvider() as any;

    // WalletConnect must establish its session before any request() calls
    if (typeof provider.connect === "function") {
      await provider.connect({ chains: [chainId] });
    }

    const accounts = (await provider.request({ method: "eth_requestAccounts" })) as string[];
    if (!accounts[0]) {
      throw new SurfError(
        SurfErrorCode.WALLET_NOT_CONNECTED,
        "WalletConnect did not return any connected account",
      );
    }

    const chainHex = (await provider.request({ method: "eth_chainId" })) as string;

    this.bindListeners(provider);

    return {
      address: getAddress(accounts[0]) as `0x${string}`,
      chainId: Number.parseInt(chainHex, 16),
      connected: true,
    };
  }
}
